import { z } from 'zod';
import { ControllerRenderProps } from 'react-hook-form';
import { RetrievalMethod } from './constants';
import { getAvailableRequestActions } from './utils/requestActions';

export type TemplateType = 'HIE' | 'FULL';

export type ProfileListItem = {
  id: string;
  organizationId: string;
  displayName: string;
  description?: string;
  avatarType: string;
  npi?: string;
  patientCount: number;
  creatorName?: string;
  createdAt: string;
};

export type DefaultRequestAction = {
  name: string;
  displayName: string;
  isEnabled: boolean;
  validationFields?: string[];
  category: 'Records & Data' | 'Analysis';
};

export type Facesheet = {
  facesheetConfigId: string;
  organizationId: string;
  organizationProfileId?: string;
  displayName: string;
  isOrgWide?: boolean;
  isDefault?: boolean;
  isExpedited?: boolean;
  templateTypes: string[];
};

export interface Profile {
  id: string;
  organizationId: string;
  displayName: string;
  description?: string;
  avatarType: string;
  npi?: string;
  patientCount: number;
  creatorName?: string;
  createdAt: string;
  defaultRetrievalMethods: string[];
  availableRetrievalMethods: string[];
  facesheets: Array<Facesheet>;
}

// Schema for creating a new profile - some fields are optional since they'll be set by the server
export const facesheetSchema = z.object({
  facesheetConfigId: z.string(),
  organizationId: z.string(),
  organizationProfileId: z.string().optional(),
  displayName: z.string(),
  isDefault: z.boolean(),
});

// Factory function to create profile schema with facesheets context and organization info
export const createProfileSchema = (
  facesheetsForValidation: Facesheet[],
  organizationInfo?: OrganizationInfo,
  profileAvailableMethods?: string[],
  profileFacesheets?: Facesheet[],
) =>
  z
    .object({
      displayName: z
        .string()
        .min(1, 'Enter a profile name.')
        .max(80, 'Profile name cannot exceed 80 characters.'),
      description: z
        .string()
        .max(255, 'Description cannot exceed 255 characters.')
        .optional(),
      avatarType: z.string(),
      defaultRetrievalMethods: z.array(z.string()),
      defaultFacesheetConfigIds: z.array(z.string()).optional(),
      hasExpeditedFacesheets: z.boolean().optional(),
      expeditedFacesheetConfigIds: z.array(z.string()).optional(),
      requestActionErrors: z.array(z.string()).optional(), // used for showing the error message on individual request action checkboxes
    })
    .check((ctx) => {
      const data = ctx.value;
      const availableActions = getAvailableRequestActions({
        orgInfo: organizationInfo,
        organizationFacesheets: facesheetsForValidation,
        profileAvailableMethods,
        profileFacesheets,
      });

      // Find dynamic action index for error paths
      const facesheetsActionIndex = availableActions.findIndex(
        (a) => a.name === RetrievalMethod.FACESHEETS,
      );

      // Facesheet validation for Digital Health Data
      const hieEnabled = data.defaultRetrievalMethods?.includes(
        RetrievalMethod.HIE,
      );
      const facesheetEnabled = data.defaultRetrievalMethods?.includes(
        RetrievalMethod.FACESHEETS,
      );

      const hasHieFacesheets = data.defaultFacesheetConfigIds?.some(
        (id) =>
          facesheetsForValidation
            .find((f) => f?.facesheetConfigId === id)
            ?.templateTypes?.includes('HIE') ?? false,
      );

      // HIE requires at least one HIE-based facesheet to be selected
      if (hieEnabled && facesheetsForValidation.length > 0) {
        if (!hasHieFacesheets) {
          ctx.issues.push({
            code: 'custom',
            message:
              'Digital Health Data requires at least one HIE-based facesheet.',
            path: ['defaultFacesheetConfigIds'],
            input: data,
          });
          ctx.issues.push({
            code: 'custom',
            message:
              'Digital Health Data requires at least one HIE-based facesheet.',
            path: ['defaultRetrievalMethods'],
            input: data,
          });
        }
      }

      // HIE-based facesheets requires HIE to be enabled
      if (hasHieFacesheets && !hieEnabled) {
        ctx.issues.push({
          code: 'custom',
          message: 'Digital Health Data is required for HIE-based facesheets.',
          path: ['requestActionErrors', 0],
          input: data,
        });
        ctx.issues.push({
          code: 'custom',
          message: 'Digital Health Data is required for HIE-based facesheets.',
          path: ['defaultRetrievalMethods'],
          input: data,
        });
      }

      // HIE requires at least one facesheet
      if (
        hieEnabled &&
        (!data.defaultFacesheetConfigIds ||
          data.defaultFacesheetConfigIds?.length === 0)
      ) {
        ctx.issues.push({
          code: 'custom',
          message: 'Digital Health Data requires at least one facesheet.',
          path: ['defaultFacesheetConfigIds'],
          input: data,
        });
      }

      // HIE requires Facesheets as a default request action (only validate if Facesheets is available)
      if (hieEnabled && !facesheetEnabled && facesheetsActionIndex !== -1) {
        ctx.issues.push({
          code: 'custom',
          message:
            'Digital Health Data requires Facesheets as a default request action.',
          path: ['defaultRetrievalMethods'],
          input: data,
        });
        ctx.issues.push({
          code: 'custom',
          message:
            'Digital Health Data requires Facesheets as a default request action.',
          path: ['requestActionErrors', facesheetsActionIndex],
          input: data,
        });
      }

      // Expedited facesheets validation
      const hasExpeditedFacesheets = data.hasExpeditedFacesheets;
      if (
        hasExpeditedFacesheets &&
        data.expeditedFacesheetConfigIds?.length === 0
      ) {
        ctx.issues.push({
          code: 'custom',
          message: 'Select at least one expedited facesheet.',
          path: ['expeditedFacesheetConfigIds'],
          input: data,
        });
      }
    });

// Type inference for the schema (needs to be created with facesheets parameter)
export type CreateProfileFormData = z.infer<
  ReturnType<typeof createProfileSchema>
>;
export type EditProfileFormData = CreateProfileFormData;

export type ProfileFormFieldProps<T extends keyof CreateProfileFormData> = {
  field: ControllerRenderProps<CreateProfileFormData, T>;
};

export type UpdateProfilePayload = {
  organizationProfileId: string;
  displayName?: string;
  description?: string;
  avatarType?: string;
  defaultRetrievalMethods?: string[];
  defaultFacesheetConfigIds?: string[];
  expeditedFacesheetConfigIds?: string[];
};

export interface OrganizationInfo {
  enableHieRequests: boolean;
  enableFacesheetRequests: boolean;
  enableFacesheetExpedited: boolean;
  enableRecordUploadRequests: boolean;
  enableAdvancedFacesheetsRequests?: boolean;
  enableAnonymousRecordFaxSearch: boolean;
}

export interface FilterParams {
  orgInfo?: OrganizationInfo;
  organizationFacesheets?: Facesheet[];
  profileAvailableMethods?: string[];
  profileFacesheets?: Facesheet[];
}
